import { useState, useEffect, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Mic, MicOff, Settings } from "lucide-react";
import ComponentCard from "../../../../../shared/ui/common/ComponentCard";
import NotionEditor, { NotionEditorHandle } from "../../../../../shared/ui/form/input/NotionEditor";
import Label from "../../../../../shared/ui/form/Label";
import Button from "../../../../../shared/ui/button/Button";
import { motion } from "framer-motion";
import { SessionTemplate, getTemplateName, getTemplateDescription } from "../../../../session_templates";
import { buildTemplateHTML, parseTemplateHTML } from "../../utils/templateDocumentUtils";
import { countNoteWords, MIN_ANALYSIS_WORDS } from "../../../domain/notesAnalysis";
import { useAiInsightsGate } from "../../../../ai_insights";
import { useTherapist } from "../../../../../context/useTherapist";
import LiveSessionSettingsModal from "./LiveSessionSettingsModal";
import { useNotesDictation } from "../../../application/useNotesDictation";
import DictationPunctuationHint from "../../../../../shared/ui/common/DictationPunctuationHint";

interface SessionNotesFormProps {
  notes: string;
  onNotesChange: (text: string) => void;
  onBack?: () => void;
  onSave?: () => void;
  template?: SessionTemplate | null;
  onTemplateChange?: (templateId: string) => void;
  allowTemplateChange?: boolean;
}

export default function SessionAddNotesForm({
  notes,
  onNotesChange,
  onBack,
  onSave,
  template,
}: SessionNotesFormProps) {
  const { t } = useTranslation();
  const { therapist } = useTherapist();

  const [appliedTemplateId, setAppliedTemplateId] = useState<string | null>(null);

  // The word threshold only exists because of the AI analysis, so it is noise
  // for a therapist without Memoria aumentada. `adopted` rather than
  // `!locked`: while adoption is still loading neither state is known, and
  // showing the requirement then retracting it is worse than showing it late.
  const { adopted: aiInsightsAdopted } = useAiInsightsGate();

  const wordCount = countNoteWords(notes);
  const hasEnoughForAnalysis = wordCount >= MIN_ANALYSIS_WORDS;

  // 🎤 Dictation State
  const [devices, setDevices] = useState<{ label: string; value: string }[]>([]);
  const [selectedDevice, setSelectedDevice] = useState<string>("");
  const [showDeviceSelector, setShowDeviceSelector] = useState(false);

  const editorRef = useRef<NotionEditorHandle>(null);

  // What is being heard is shown provisionally and then *replaced* by the
  // settled phrase — writing both is what duplicated every spoken sentence.
  const showInterim = useCallback((text: string) => {
    editorRef.current?.setInterimText(text);
  }, []);
  const writeSettled = useCallback((text: string) => {
    editorRef.current?.commitInterim(text);
  }, []);
  const dictationLanguage = therapist?.language === "es" ? "es" : "en";
  const dictation = useNotesDictation({
    language: dictationLanguage,
    onInterim: showInterim,
    onFinal: writeSettled,
  });

  // Initialize Microphone Devices
  useEffect(() => {
    const getDevices = async () => {
      try {
        const devs = await navigator.mediaDevices.enumerateDevices();
        const audioDevs = devs
          .filter((d) => d.kind === "audioinput")
          .map((d) => ({
            label: d.label || `${t("session.live.microphone", "Microphone")} ${d.deviceId.substring(0, 4)}...`,
            value: d.deviceId,
          }));
        setDevices(audioDevs);
        if (audioDevs.length > 0 && !selectedDevice) {
          setSelectedDevice(audioDevs[0].value);
        }
      } catch (e) {
        console.error("Error fetching devices", e);
      }
    };
    getDevices();
  }, [t, selectedDevice]);

  // Handle Dictation
  const toggleDictation = () => {
    if (!dictation.supported) {
      alert(t("session.dictation.notSupported", "Speech recognition is not supported in this browser."));
      return;
    }
    dictation.toggle();
  };

  // Build the unified document when template changes
  useEffect(() => {
    if (!template || template.fields.length === 0) return;

    // Apply the template only if it's the first match or explicitly changed
    // By checking template.id !== appliedTemplateId, we guarantee we wait for the NEW template object to arrive
    // before we generate its empty HTML structure.
    if (template.id !== appliedTemplateId) {
      const isManualChange = appliedTemplateId !== null;
      const existingValues: Record<string, string> = {};
      const fields = [...template.fields].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

      if (notes && !isManualChange) {
        // Attempt to parse an existing unified HTML doc first
        const parsed = parseTemplateHTML(notes, fields);
        const hasContent = Object.values(parsed).some(v => v && v !== "<p></p>");
        if (hasContent) {
          onNotesChange(buildTemplateHTML(fields, parsed, t, therapist?.language));
          setAppliedTemplateId(template.id || 'unknown');
          return;
        }

        // Fallback: try legacy **Field**: value markdown format
        fields.forEach(field => {
          const escapedName = field.name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
          const headerRegex = new RegExp(`\\*\\*${escapedName}\\*\\*\\s*:?`, 'i');
          const match = notes.match(headerRegex);
          if (match && match.index !== undefined) {
            const startPos = match.index + match[0].length;
            let endPos = notes.length;
            fields.forEach(nextField => {
              if (nextField.name === field.name) return;
              const nextEscaped = nextField.name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
              const nextRegex = new RegExp(`\\*\\*${nextEscaped}\\*\\*\\s*:?`, 'i');
              const nextMatch = notes.substring(startPos).match(nextRegex);
              if (nextMatch && nextMatch.index !== undefined) {
                const abs = startPos + nextMatch.index;
                if (abs < endPos) endPos = abs;
              }
            });
            existingValues[field.id!] = notes.substring(startPos, endPos).trim();
          } else {
            existingValues[field.id!] = "";
          }
        });
      } else {
        fields.forEach(f => { existingValues[f.id!] = ""; });
      }

      const html = buildTemplateHTML(fields, existingValues, t, therapist?.language);
      onNotesChange(html);
      setAppliedTemplateId(template.id || 'unknown');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [template, appliedTemplateId, t, therapist?.language]);

  return (
    <ComponentCard title={t("session.sessionNotes")}>
      <div className="space-y-6">
        {/* Template Header / Selector */}
        <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
          <div className="flex justify-between items-start mb-2">
            <div>
              <Label className="text-xs uppercase tracking-wider text-gray-500 font-semibold mb-1">
                {t("templates.activeTemplate", "Active Template")}
              </Label>
              <h4 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                {template ? getTemplateName(template, t) : t("templates.noTemplate", "No Template")}
              </h4>
            </div>
          </div>

          {template?.description && (
            <p className="text-sm text-gray-500">{getTemplateDescription(template, t)}</p>
          )}

          {/* Dictation Controls */}
          <div className="mt-4 space-y-2">
            <div className="flex flex-wrap items-center gap-3">
              <button
                onClick={toggleDictation}
                className={`
                  group relative flex items-center gap-2 px-4 py-2 rounded-xl font-bold text-xs uppercase tracking-wider transition-all
                  ${dictation.isDictating
                    ? "bg-purple-100 text-purple-700 ring-2 ring-purple-500/20"
                    : "bg-white border border-gray-200 text-gray-600 hover:border-purple-200 hover:text-purple-600"}
                `}
              >
                {dictation.isDictating && (
                  <motion.span
                    animate={{ scale: [1, 1.2, 1], opacity: [0.5, 1, 0.5] }}
                    transition={{ repeat: Infinity, duration: 1.5 }}
                    className="w-2 h-2 rounded-full bg-purple-600"
                  />
                )}
                {dictation.isDictating ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
                  {dictation.isDictating ? t("session.dictation.stop") : t("session.dictation.start")}
              </button>

              <DictationPunctuationHint language={dictationLanguage} />

              <button
                onClick={() => setShowDeviceSelector(!showDeviceSelector)}
                className={`p-2 rounded-xl border transition-all ${showDeviceSelector ? "bg-gray-100 border-gray-300" : "bg-white border-gray-200 hover:bg-gray-50"}`}
                title={t("session.live.selectMic", "Select microphone")}
              >
                <Settings className="w-4 h-4 text-gray-500" />
              </button>
            </div>
          </div>

          <LiveSessionSettingsModal
            isOpen={showDeviceSelector}
            onClose={() => setShowDeviceSelector(false)}
            devices={devices}
            selectedDevice={selectedDevice}
            onSelectDevice={setSelectedDevice}
            reminderEnabled={false} // Not needed for dictation
            onToggleReminder={() => {}}
            reminderMinutes={0}
            onChangeReminderMinutes={() => {}}
            showSessionLimit={false}
            disabled={dictation.isDictating}
          />
        </div>

        {/* Notes Editor — Unified */}
        <div className={template && template.fields.length > 0 ? "mt-6" : ""}>
          {(!template || template.fields.length === 0) && (
            <Label className="mb-2 block">{t("session.sessionNotes")}</Label>
          )}
          <NotionEditor
            ref={editorRef}
            value={notes}
            onChange={onNotesChange}
            placeholder={template && template.fields.length > 0 ? t("notionEditor.placeholder") : t("session.notesPlaceholder")}
            hint={template && template.fields.length > 0 ? "" : t("session.notesHint")}
            className={template && template.fields.length > 0 ? "min-h-[300px]" : ""}
          />

          {/* Analysis requirements — stated up front, with live progress, so the
              therapist is not told after saving that the notes were too short.
              Only shown when Memoria aumentada is adopted: without it no
              analysis runs, so the word threshold means nothing. */}
          {aiInsightsAdopted && (
          <div className="mt-2 flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
            <p className="text-xs text-gray-500">
              {t("session.notesRequirements", { min: MIN_ANALYSIS_WORDS })}
            </p>
            <p
              aria-live="polite"
              className={`text-xs font-medium tabular-nums ${
                hasEnoughForAnalysis ? "text-green-600" : "text-gray-500"
              }`}
            >
              {/* `words`, not `count`: i18next treats `count` as a plural
                  selector and would look for suffixed keys that do not exist. */}
              {hasEnoughForAnalysis
                ? t("session.notesWordCountReady", { words: wordCount })
                : t("session.notesWordCountShort", {
                    words: wordCount,
                    min: MIN_ANALYSIS_WORDS,
                    remaining: MIN_ANALYSIS_WORDS - wordCount,
                  })}
            </p>
          </div>
          )}
        </div>

        <div className="flex justify-center gap-2 pt-4">
          <Button variant="outline" onClick={onBack}>
            {t("session.back")}
          </Button>
          <Button onClick={onSave}>
            {t("session.continue")}
          </Button>
        </div>
      </div>
    </ComponentCard>
  );
}
